import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Play } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageShell from "@/components/PageShell";
import EmailCapture from "@/components/EmailCapture";
import { findArticle, articles } from "@/data/articles";
import { activeOffer } from "@/data/offer";
import { articleGoPath } from "@/lib/golinks";
import { trackEvent, installEngagementTracking } from "@/lib/tracking";

// Custom <verse> tag handling — wraps Devanagari verse blocks in a styled
// blockquote so Sanskrit / Pali stand visually distinct from prose.
const components = {
  // Render <verse>...</verse> blocks (we emit these from the normalize pass)
  // as a centered, gold-tinted Devanagari block.
  verse: ({ children }) => (
    <blockquote className="my-8 not-italic font-serif text-xl md:text-2xl text-dharma-gold-deep text-center leading-loose tracking-wider">
      {children}
    </blockquote>
  ),
  // Pull-quote / blockquote styling
  blockquote: ({ children }) => (
    <blockquote className="my-8 border-l-2 border-dharma-gold pl-6 md:pl-8 font-serif text-xl md:text-2xl italic text-ink leading-relaxed">
      {children}
    </blockquote>
  ),
  // Headings — Cormorant for h2/h3, hierarchy via size
  h2: ({ children }) => (
    <h2 className="font-display text-2xl md:text-3xl text-ink mt-12 mb-4 tracking-tightish leading-tight">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-display text-xl md:text-2xl text-ink mt-8 mb-3 tracking-tightish">
      {children}
    </h3>
  ),
  // Body paragraphs — generous serif body
  p: ({ children }) => (
    <p className="font-serif text-ink-soft text-lg md:text-xl leading-[1.75] my-5">
      {children}
    </p>
  ),
  // Lists
  ul: ({ children }) => (
    <ul className="font-serif text-ink-soft text-lg md:text-xl leading-[1.75] my-5 ml-6 list-disc space-y-2">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="font-serif text-ink-soft text-lg md:text-xl leading-[1.75] my-5 ml-6 list-decimal space-y-2">
      {children}
    </ol>
  ),
  // Inline / block images — full-width, captioned via alt text
  img: ({ src, alt }) => (
    <span className="block my-10 -mx-5 md:-mx-10 lg:-mx-14">
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        className="w-full h-auto"
      />
      {alt ? (
        <span className="block font-serif italic text-ink-mute text-sm md:text-base text-center mt-3 px-5 md:px-10">
          {alt}
        </span>
      ) : null}
    </span>
  ),
  // Inline emphasis
  strong: ({ children }) => (
    <strong className="text-ink font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  // Links — open external in new tab
  a: ({ href, children }) => {
    const external = href?.startsWith("http") || href?.startsWith("mailto");
    return external ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-dharma-gold-deep hover:text-saffron-fire underline decoration-dharma-gold/40 underline-offset-4 transition-colors"
      >
        {children}
      </a>
    ) : (
      <Link
        to={href}
        className="text-dharma-gold-deep hover:text-saffron-fire underline decoration-dharma-gold/40 underline-offset-4 transition-colors"
      >
        {children}
      </Link>
    );
  },
};

// `variant` picks which surface this article is being read on.
//
//   "writing" — the site's own essays section. A browsing reader; the footer
//               offers the newsletter form, and the newsletter form is fine
//               because nothing here is being measured against a spend.
//
//   "x"       — the landing surface for X and Substack traffic. The strategy
//               allows this footer EXACTLY ONE tagged link ("each footer
//               carries exactly one tagged link to the primary SKU or /go"),
//               and it is routed through /go so the click is counted at our own
//               door. Two calls to action here would not be generosity; they
//               would split the only signal the article can produce.
export default function WritingDetail({ variant = "writing" }) {
  const { slug } = useParams();
  const article = findArticle(slug);
  const isX = variant === "x";
  const base = isX ? "/x" : "/writing";
  const offer = activeOffer();

  // Engagement tracking (scroll depth + dwell) once article is mounted
  useEffect(() => {
    if (!article) return;
    trackEvent("article_view", {
      title_id: article.id,
      slug: article.id,
      article_title: article.title,
      surface: variant,
    });
    const cleanup = installEngagementTracking({
      title_id: article.id,
      slug: article.id,
    });
    return cleanup;
  }, [article, variant]);

  if (!article) return <Navigate to={base} replace />;

  // Index of the article + adjacent for "next/prev" cross-link
  const idx = articles.findIndex((a) => a.id === article.id);
  const prev = idx > 0 ? articles[idx - 1] : null;
  const next = idx < articles.length - 1 ? articles[idx + 1] : null;

  // Strip the leading h1 from body_md since we render the title separately
  // in the hero section. Match "# Title\n" at the very top.
  const body = article.body_md.replace(/^#\s+.*\n+/, "");

  return (
    <PageShell title={article.title}>
      {/* Hero band — dark shrine with title + pull-quote + hero image */}
      <section className="bg-shrine-shadow text-cream pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-4xl mx-auto px-5 md:px-10 lg:px-14">
          <Link
            to={base}
            className="inline-flex items-center gap-1.5 font-body text-xs text-cream/60 hover:text-dawn-rose tracking-widest2 uppercase transition-colors mb-6"
          >
            <ArrowLeft size={14} /> {isX ? "All essays" : "All writing"}
          </Link>
          <p className="eyebrow text-dawn-rose mb-4">{article.subtitle}</p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-cream text-balance text-3xl md:text-5xl lg:text-6xl tracking-tightish leading-[1.05] max-w-3xl"
          >
            {article.title}
          </motion.h1>
          {article.pull_quote && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif italic text-cream/80 text-lg md:text-2xl mt-6 max-w-2xl leading-relaxed"
            >
              {article.pull_quote}
            </motion.p>
          )}
        </div>
      </section>

      {/* Hero image — pulled out of the dark band so it bridges */}
      <div className="bg-shrine-shadow">
        <div className="max-w-5xl mx-auto px-0 md:px-10 lg:px-14">
          <img
            src={article.hero_url}
            alt={article.title}
            className="w-full h-auto"
          />
        </div>
      </div>

      {/* Body */}
      <section className="bg-cream py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-10 lg:px-14">
          {/* Film CTA — only renders when the article entry carries a
              youtube_url. Sits at the top of the body so it's visible
              before the long-read begins, but doesn't compete with the
              hero. Pixel + GA4 see the click as `film_pill_click`. */}
          {article.youtube_url ? (
            <a
              href={article.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("film_pill_click", {
                title_id: article.id,
                slug: article.id,
                article_title: article.title,
                youtube_url: article.youtube_url,
              })}
              className="group inline-flex items-center gap-3 mb-10 md:mb-14
                         px-5 py-3 rounded-full border border-dharma-gold/40
                         bg-dharma-gold/8 hover:bg-dharma-gold/15
                         text-ink hover:text-dharma-gold-deep
                         transition-colors no-underline"
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-full
                               bg-dharma-gold-deep text-cream group-hover:scale-105 transition-transform">
                <Play size={16} fill="currentColor" />
              </span>
              <span className="font-display text-base md:text-lg tracking-tightish">
                Watch the {article.film_duration_label || "film"}
              </span>
              <span className="font-body text-xs text-ink-mute tracking-widest2 uppercase">
                on YouTube ↗
              </span>
            </a>
          ) : null}

          <article className="prose-article">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {body}
            </ReactMarkdown>
          </article>
        </div>
      </section>

      {/* The foot of the article. On /x this is the one tagged link the
          strategy allows; on /writing it stays the newsletter form. */}
      <section className="bg-parchment/40 py-14 md:py-20 border-t border-dharma-gold/15">
        <div className="max-w-2xl mx-auto px-5 md:px-10 lg:px-14 text-center">
          {isX ? (
            <>
              <p className="eyebrow mb-4">{offer.eyebrow}</p>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-tightish text-balance">
                {offer.title}
              </h3>
              <p className="font-serif text-ink-soft text-lg mt-4 leading-relaxed text-pretty">
                {offer.subtitle}
              </p>
              <a
                href={articleGoPath(article.id)}
                onClick={() =>
                  trackEvent("magnet_link_click", {
                    title_id: offer.sku,
                    slug: offer.id,
                    offer_id: offer.id,
                    surface: "x",
                    article_id: article.id,
                  })
                }
                className="btn-pill btn-pill-solid mt-8"
              >
                {offer.deliverable.label}
              </a>
            </>
          ) : (
            <>
              <p className="eyebrow mb-4">If this resonated</p>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-tightish max-w-md mx-auto">
                The next one is for you.
              </h3>
              <div className="mt-8 max-w-md mx-auto text-left">
                <EmailCapture
                  titleId={`writing-${article.id}`}
                  slug={`writing-${article.id}`}
                  prompt="One letter when the next essay lands. Nothing else."
                />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Prev / Next cross-link — light navigation between articles */}
      {(prev || next) && (
        <section className="bg-cream py-12 md:py-16 border-t border-dharma-gold/15">
          <div className="max-w-5xl mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {prev ? (
              <Link
                to={`${base}/${prev.id}`}
                className="group block"
                onClick={() => trackEvent("article_nav_click", { from: article.id, to: prev.id, dir: "prev", surface: variant })}
              >
                <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase mb-2">
                  ← Previous
                </p>
                <p className="font-display text-xl md:text-2xl text-ink group-hover:text-dharma-gold-deep transition-colors leading-tight tracking-tightish">
                  {prev.title}
                </p>
              </Link>
            ) : <div />}
            {next ? (
              <Link
                to={`${base}/${next.id}`}
                className="group block md:text-right"
                onClick={() => trackEvent("article_nav_click", { from: article.id, to: next.id, dir: "next", surface: variant })}
              >
                <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase mb-2">
                  Next →
                </p>
                <p className="font-display text-xl md:text-2xl text-ink group-hover:text-dharma-gold-deep transition-colors leading-tight tracking-tightish">
                  {next.title}
                </p>
              </Link>
            ) : <div />}
          </div>
        </section>
      )}
    </PageShell>
  );
}
